import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { useState } from 'react';
import { useGetPublicTestimonialsQuery } from '../../store/slices/apiSlice';
import { Star, MapPin, User, Shield, Heart, MessageSquare } from 'lucide-react';

interface TestimonialsSectionProps {
    title?: string;
    subtitle?: string;
    maxItems?: number;
    showAll?: boolean;
    className?: string;
}

const TestimonialsSection = ({ 
    title = "What Our Customers Say", 
    subtitle = "Real testimonials from satisfied customers",
    maxItems = 3,
    showAll = false,
    className = ""
}: TestimonialsSectionProps) => {
    const { data, isLoading, error } = useGetPublicTestimonialsQuery();

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`h-4 w-4 ${
                    index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                }`}
            />
        ));
    };

    const getCategoryColor = (category: string) => {
        switch (category.toLowerCase()) {
            case 'laundry': return 'bg-blue-100 text-blue-800';
            case 'investment': return 'bg-green-100 text-green-800';
            case 'premium': return 'bg-purple-100 text-purple-800';
            case 'general': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category.toLowerCase()) {
            case 'laundry': return '👕';
            case 'investment': return '💰';
            case 'premium': return '⭐';
            case 'general': return '💬';
            default: return '💬';
        }
    };

    if (isLoading) {
        return (
            <div className={`py-16 ${className}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
                        <p className="text-lg text-gray-600">{subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: maxItems }).map((_, index) => (
                            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
                                <div className="flex items-center mb-4">
                                    <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                                    <div className="ml-4 flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-3 bg-gray-200 rounded"></div>
                                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`py-16 ${className}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <MessageSquare className="h-12 w-12 text-red-600 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Testimonials</h3>
                            <p className="text-red-600">There was an error loading customer testimonials. Please try again later.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const testimonials = data?.data?.testimonials || [];
    const approvedTestimonials = testimonials.filter(testimonial => testimonial.status === 'approved');
    const displayTestimonials = showAll ? approvedTestimonials : approvedTestimonials.slice(0, maxItems);

    if (displayTestimonials.length === 0) {
        return (
            <div className={`py-16 ${className}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12">
                            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Testimonials Yet</h3>
                            <p className="text-gray-600">Be the first to share your experience with us!</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`py-16 ${className}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
                    <p className="text-lg text-gray-600">{subtitle}</p>
                    {!showAll && approvedTestimonials.length > maxItems && (
                        <p className="text-sm text-gray-500 mt-2">
                            Showing {maxItems} of {approvedTestimonials.length} testimonials
                        </p>
                    )}
                </div>

                {/* Testimonials Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {displayTestimonials.map((testimonial) => (
                        <div key={testimonial.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl hover:border-blue-100 transition-all duration-300 transform hover:-translate-y-1">
                            {/* Customer Info */}
                            <div className="flex items-start mb-6">
                                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                                    {testimonial.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="ml-4 flex-1 min-w-0 overflow-hidden">
                                    <h4 className="font-bold text-gray-900 text-lg mb-2 truncate">{testimonial.name}</h4>
                                    <div className="flex items-center text-sm text-gray-500 mb-3">
                                        <MapPin className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
                                        <span className="truncate">{testimonial.location}</span>
                                    </div>
                                    {testimonial.verified && (
                                        <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
                                            <Shield className="h-3 w-3 mr-1" />
                                            Verified Customer
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Rating */}
                            <div className="flex items-center mb-5">
                                <div className="flex items-center">
                                    {renderStars(testimonial.rating)}
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                                    {testimonial.rating}/5
                                </span>
                            </div>

                            {/* Testimonial Text */}
                            <blockquote className="text-gray-700 mb-6 leading-relaxed text-lg font-medium italic break-words overflow-hidden">
                                "{testimonial.text}"
                            </blockquote>

                            {/* Category and Helpful */}
                            <div className="flex items-center justify-between mb-4 overflow-hidden">
                                <div className="flex items-center min-w-0 flex-1">
                                    <span className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold ${getCategoryColor(testimonial.category)} shadow-sm truncate`}>
                                        <span className="mr-2">{getCategoryIcon(testimonial.category)}</span>
                                        <span className="truncate">{testimonial.category}</span>
                                    </span>
                                </div>
                                {testimonial.helpful > 0 && (
                                    <div className="flex items-center text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full flex-shrink-0 ml-2">
                                        <Heart className="h-4 w-4 mr-1 text-red-400" />
                                        {testimonial.helpful}
                                    </div>
                                )}
                            </div>

                            {/* Date */}
                            <div className="pt-4 border-t border-gray-100">
                                <p className="text-xs text-gray-400 font-medium">
                                    {formatDateUtil(testimonial.created_at)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Show More Button (if not showing all) */}
                {!showAll && approvedTestimonials.length > maxItems && (
                    <div className="text-center mt-12">
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            View All Testimonials ({approvedTestimonials.length})
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TestimonialsSection;
